import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Gavel, FileText, Search, MapPin, Landmark, UploadCloud } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import Breadcrumbs from '../components/Breadcrumbs';
import { QuoteForm } from '../components/quote/QuoteForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOAD_ERROR = 'Unable to load the quote form. Please refresh the page or email quote@legaldocspecialist.com.';

const fetchQuoteForms = async () => {
  const response = await fetch(`${BACKEND_URL}/api/quote-forms`);
  if (!response.ok) throw new Error(LOAD_ERROR);
  return response.json();
};

const ICONS = { 'process-serving': Gavel, 'court-filing': FileText, 'e-filing': UploadCloud, 'document-retrieval': Search, 'skip-tracing': MapPin, 'county-recording': Landmark };
const WHY = {
  'process-serving': 'Document type, deadline, address and whether sub-service is allowed all change how many attempts and how much travel a job needs.',
  'court-filing': 'The court location, filing type and deadline determine travel, wait time and whether a same-day run is required.',
  'e-filing': 'Document count, readiness and court fees determine the e-filing provider charges and how quickly we can submit.',
  'document-retrieval': 'Where the record is held, how many copies and whether certification is needed drive the agency fees and turnaround.',
  'skip-tracing': 'The more identifiers you can share, the faster and cheaper the search - and the more accurate the result.',
  'county-recording': 'County, document type, notarization and transfer-tax status determine recording fees and whether we can record same day.'
};
const ORDER = ['process-serving', 'court-filing', 'e-filing', 'document-retrieval', 'skip-tracing', 'county-recording'];

const ServiceTab = ({ slug, schema, active, onClick }) => {
  const Icon = ICONS[slug] || FileText;
  return (
    <button type="button" role="tab" aria-selected={active} data-testid={`quote-tab-${slug}`} onClick={onClick} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem', padding: '1.25rem', textAlign: 'left', cursor: 'pointer',
      borderRadius: '12px', border: `2px solid ${active ? 'var(--brand-primary)' : 'var(--border-medium)'}`,
      backgroundColor: active ? 'var(--brand-primary)' : 'var(--bg-card)', color: active ? '#fff' : 'var(--text-primary)',
      transition: 'transform 0.15s, border-color 0.15s, background-color 0.15s', transform: active ? 'translateY(-2px)' : 'none'
    }}>
      <Icon size={22} color={active ? '#fff' : 'var(--brand-primary)'} />
      <span style={{ fontWeight: '700', fontSize: '1rem' }}>{schema.label}</span>
      <span style={{ fontSize: '0.8rem', opacity: active ? 0.9 : 0.75, lineHeight: 1.4 }}>{schema.short}</span>
    </button>
  );
};

const QuotePage = () => {
  const [params, setParams] = useSearchParams();
  const [forms, setForms] = useState(null);
  const [loadError, setLoadError] = useState('');
  const requested = params.get('service');
  const service = ORDER.includes(requested) ? requested : 'process-serving';

  useEffect(() => {
    let cancelled = false;
    fetchQuoteForms()
      .then((data) => { if (!cancelled) setForms(data); })
      .catch(() => { if (!cancelled) setLoadError(LOAD_ERROR); });
    return () => { cancelled = true; };
  }, []);

  const prefillEmail = params.get('email') || '';
  const selectService = (slug) => setParams(prefillEmail ? { service: slug, email: prefillEmail } : { service: slug });

  return (
    <div>
      <SEOHelmet
        title="Request a Quote | Process Serving, Court Filing & More | Legal Document Specialist"
        description="Answer a few service-specific questions and get a tailored quote for process serving, court filing, document retrieval, skip tracing, or county recording in California."
        keywords="process server quote, court filing quote, legal document services quote California"
        canonical="/quote"
      />
      <Header />
      <Breadcrumbs />

      <section style={{ backgroundColor: 'var(--bg-page)', padding: '150px 0 60px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '760px' }}>
            <p style={{ color: 'var(--brand-primary)', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.8rem', marginBottom: '1rem' }}>Quote Questionnaire</p>
            <h1 className="heading-1" data-testid="quote-page-title" style={{ marginBottom: '1.25rem', color: 'var(--text-primary)', fontSize: 'clamp(2.2rem, 4vw, 3.5rem)', lineHeight: 1.1 }}>
              Tell us the specifics. We'll send an exact quote.
            </h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Pick the service you need and answer a few targeted questions. Every quote is prepared by hand and emailed within 24 hours.
            </p>
          </div>
        </div>
      </section>

      <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0 100px 0' }}>
        <div className="container">
          {loadError && <p data-testid="quote-load-error" style={{ color: '#c53030' }}>{loadError}</p>}
          {!forms && !loadError && <p data-testid="quote-loading" style={{ color: 'var(--text-secondary)' }}>Loading questionnaire...</p>}
          {forms && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '2.5rem' }}>
              <div role="tablist" aria-label="Select a service" data-testid="quote-service-tabs" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1rem' }}>
                {ORDER.map((slug) => (
                  <ServiceTab key={slug} slug={slug} schema={forms.services[slug]} active={slug === service} onClick={() => selectService(slug)} />
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(260px, 1fr)', gap: '2.5rem', alignItems: 'start' }} className="quote-layout">
                <div>
                  <h2 className="heading-2" data-testid="quote-active-service" style={{ marginBottom: '1.5rem', fontSize: '1.6rem' }}>
                    {forms.services[service].label} Quote
                  </h2>
                  <QuoteForm key={service} service={service} schema={forms.services[service]} contactFields={forms.contact} initialContact={prefillEmail ? { email: prefillEmail } : {}} />
                </div>
                <aside style={{ position: 'sticky', top: '110px', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div style={{ padding: '1.75rem', borderRadius: '12px', border: '1px solid var(--border-medium)', backgroundColor: 'var(--bg-page)' }}>
                    <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: '700', color: 'var(--text-primary)' }}>Why we ask</h3>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                      {WHY[service]} Answering now means your quote is accurate the first time.
                    </p>
                  </div>
                </aside>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />

      <style>{`
        @media (max-width: 900px) {
          .quote-layout { grid-template-columns: 1fr !important; }
          .quote-layout aside { position: static !important; }
        }
        @media (max-width: 640px) {
          .quote-grid > div { grid-column: span 12 !important; }
        }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default QuotePage;
